import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * useKeyboardShortcuts
 *
 * Two-key "go to" shortcuts for power users. Press 'g' followed by a key:
 *   g + d → Dashboard
 *   g + j → Journal
 *   g + s → Step Work
 *   g + c → Check-in
 *   g + r → Read (Big Book)
 *   g + p → Settings (preferences)
 *
 * Disabled when the user is focused on a text input or textarea.
 */
export function useKeyboardShortcuts() {
  const navigate = useNavigate();
  const pendingG = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    function isInputFocused() {
      const tag = document.activeElement?.tagName.toLowerCase();
      const role = document.activeElement?.getAttribute('role');
      return tag === 'input' || tag === 'textarea' || tag === 'select' || role === 'textbox';
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (isInputFocused()) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const key = e.key.toLowerCase();

      if (pendingG.current) {
        pendingG.current = false;
        if (timer.current) clearTimeout(timer.current);

        switch (key) {
          case 'd': navigate('/dashboard'); break;
          case 'j': navigate('/journal'); break;
          case 's': navigate('/steps'); break;
          case 'c': navigate('/checkin'); break;
          case 'r': navigate('/read'); break;
          case 'p': navigate('/settings'); break;
        }
        return;
      }

      if (key === 'g') {
        pendingG.current = true;
        // Reset if no second key arrives within 1 second
        timer.current = setTimeout(() => {
          pendingG.current = false;
        }, 1000);
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (timer.current) clearTimeout(timer.current);
    };
  }, [navigate]);
}
